import http from '@/services/http';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * The queue endpoint takes the SAME `source` node the export endpoint takes, so
 * the two share this type. It describes "which list, behind which filter",
 * which is a question neither exports nor queues own.
 */
export type TrackListSourceRef = TrackListExportSource;

export interface TrackListQueueEntryPayload {
    kind: 'live_track';
    code: string;
    live_track_id: number;
    title: string | null;
    artist: string | null;
    album: string | null;
    /**
     * The records behind the two names, for the player bar's artist and album
     * links. Null is a real answer — a track need not have either.
     *
     * Optional on the TYPE, required on the wire: the endpoint sends both keys
     * on every entry, but a client running against a backend from before
     * tabasco#688 gets neither, and that must read as "no link" rather than
     * `undefined` leaking into an id. See the fallback in `readRecordId`.
     */
    artist_id?: number | null;
    album_id?: number | null;
    cover_art_signed_url: string | null;
    /**
     * The playlist's own 1-based numbering, or null for a list that has none (a
     * search result set, a bucket). It is what distinguishes two rows that share
     * a `code` — a generated playlist may name the same track more than once.
     */
    position: number | null;

    /**
     * Everything below is what a track CARD reads, so the queue can render the
     * same `TrackCard` a track list renders at the same three densities.
     *
     * These names are `PlaylistTrackRow`'s names, deliberately: both shapes feed
     * one card, and the queue maps a payload straight onto that row type. The
     * backend asserts the same parity from its side
     * (`TrackListQueueEntryPresenterTest`), so a rename fails a test rather than
     * quietly dropping a field from whichever surface forgot it.
     *
     * Optional on the TYPE, required on the wire — the same split, and the same
     * reason, as `artist_id` / `album_id` above. The endpoint sends every key on
     * every entry, but a client running against a backend from before
     * tabasco#689 gets none of them, and that has to read as "no card" rather
     * than a card full of `undefined`. `cardFromPayload` normalises them.
     */
    name?: string | null;
    remix_name?: string | null;
    genre_name?: string | null;
    rating?: string | null;
    isrc?: string | null;
    bitrate?: number | null;
    duration?: number | null;
    copyright?: number | null;
    playlist_count?: number | null;
    uploaded_date?: string | null;
    created_at?: string | null;
    tags?: Array<{ id: number; display_string: string }>;
    /** Named and linkable, so the card's chip can open this track's Lyrics view. */
    lyrics_check_status?: { id: number; display_string: string } | null;
}

export interface TrackListQueueResponse {
    entries: TrackListQueueEntryPayload[];
    meta: {
        total: number;
        returned: number;
        max_entries: number;
        truncated: boolean;
        label: string;
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Fetch the playable queue behind a track list.
 *
 * The envelope is verified rather than trusted: a 2xx that didn't parse must
 * fail HERE, where the caller can fall back to playing the clicked track alone,
 * not three layers down inside the player.
 */
export async function fetchTrackListQueue(source: TrackListSourceRef): Promise<TrackListQueueResponse> {
    const { data } = await http.post<unknown>('/api/track-list-queue', { source });

    if (!isRecord(data) || !Array.isArray(data.entries) || !isRecord(data.meta)) {
        throw new Error('Unexpected response from the track queue endpoint.');
    }

    return data as unknown as TrackListQueueResponse;
}
